/**
 * DTOs for the same-origin BFF (`/api/*`). These are the ONLY shapes the client
 * sees — no upstream service field, hostname, or credential ever appears here.
 */

import type { EducationLevel, Nationality } from './inbox-query.js';

/** Uniform error envelope. Raw upstream errors stay in server logs. */
export interface ApiError {
  code: string;
  message: string;
  retryable: boolean;
}

/** POST /api/session — request carries the handoff token from the bridge init. */
export interface CreateSessionRequest {
  handoffToken: string;
}

/** POST /api/session — response. sessionToken is kept in memory only by the client. */
export interface CreateSessionResponse {
  sessionToken: string;
  expiresInSeconds: number;
  /**
   * What this account may use. Computed server-side from the verified identity,
   * so the client renders the same answer the routes enforce — it is a hint for
   * the UI, never the gate itself (see the BFF's `requireEventAccess`).
   */
  features: Features;
  /**
   * Third-party endpoints the client must name but must never hardcode.
   *
   * Server-held and handed over here rather than given a `VITE_` prefix: these
   * are other apps' hostnames, and no upstream hostname may reach the built
   * bundle. Absent, or a member left empty, means that endpoint is not
   * configured — never that the client should guess one.
   */
  endpoints?: Endpoints;
  /** Runtime-tunable numbers, same lane as `endpoints`. Absent on a server
   *  from before the field existed; the client falls back to its defaults. */
  limits?: Limits;
  /**
   * The id this account's analytics are filed under — the same string the BFF
   * stamps on every relayed event.
   *
   * Handed over rather than left for the client to dig out of its session JWT,
   * so the one rule that matters is stated in one place: session replay records
   * under THIS, or replays and events land under two different people in
   * PostHog and cannot be joined, which is the only reason to record.
   *
   * It is an account id, not a credential — knowing it grants nothing, because
   * every route derives identity from the verified session and never from
   * request input. It is also a COMPANY, not a person: colleagues sharing an
   * employer share this id.
   */
  analyticsId?: string;
  /**
   * The session's company (AI-3134). Company-keyed reports use this, never the
   * Stream user id: after the identity cutover that is the recruiter, `emp_…`.
   */
  companyId?: string;
}

/** Per-account feature access. One flag per gated feature; absent means off. */
export interface Features {
  /** Booking a Chat Day from the calendar: whitelisted companies only. */
  booking: boolean;
  /** Accepted a marketing Chat Day, so its list is kept filled. */
  chatDay: boolean;
  /** The Contacts PoC: whitelisted companies only. Optional so removing it touches no fixture. */
  contacts?: boolean;
}

/** See `CreateSessionResponse.endpoints`. */
export interface Endpoints {
  /**
   * A path, not a URL, and no trailing slash: chat-js serves two brands from one
   * app, so a path resolves against whichever the jobseeker is on and keeps the
   * request same-origin. Empty means the invite goes out without a button row.
   */
  chatDayResponder: string;
}

/**
 * Product limits the server tunes at runtime (env), riding the session for the
 * same reason `endpoints` does: a number that must not be baked into the
 * bundle, so changing it is a secret set, not a rebuild.
 */
export interface Limits {
  /**
   * The most questions a form may hold. Ours independently of chat-kit's own
   * cap: the editor withholds past it and the BFF refuses past it, so the
   * limit survives even if chat-kit's is lifted. FORM_MAX_FIELDS, default 20.
   */
  formMaxFields: number;
}

/** GET /api/chat/token — Stream user token minted from the verified session identity. */
export interface ChatTokenResponse {
  apiKey: string;
  userId: string;
  token: string;
}

/**
 * GET /api/chat/pending-replies — how many chats are waiting on a reply from the
 * session user, i.e. chats where someone else sent the last message. Answered
 * from stream-chat-backend's pre-aggregated table, so the closed widget shows a
 * badge without a live Stream connection (no MAU / concurrent-connection cost).
 *
 * Not an unread count: reading a message does not clear it, only replying does.
 */
export interface PendingReplyCountResponse {
  count: number;
}

/**
 * GET /api/chat/unread — `UNREAD_CHATS_QUERY`'s total, i.e. the inbox's Unread
 * pill, answered by the BFF so a closed widget costs no MAU or connection. A
 * second endpoint rather than a field on `PendingReplyCountResponse` because
 * reading clears this and only replying clears that.
 */
export interface UnreadCountResponse {
  count: number;
}

/** One turn of the recent transcript sent to the quick-replies generator. */
export interface QuickReplyTurn {
  role: 'employer' | 'applicant';
  text: string;
}

/** POST /api/chat/quick-replies — recent transcript in, AI-suggested openers out. */
export interface QuickRepliesRequest {
  messages: QuickReplyTurn[];
}

/** One AI-suggested reply. The `id` is the upstream suggestion id, needed to
 *  report back which one the employer picked. */
export interface QuickReply {
  id: string;
  text: string;
}

/**
 * Suggested replies; empty when unavailable, so the client falls back to static.
 *
 * `auditId` ties this batch to the upstream audit row. It is what makes a pick
 * attributable — without it a selection cannot be matched to what was shown.
 * Absent when suggestions are disabled or generation failed.
 */
export interface QuickRepliesResponse {
  replies: QuickReply[];
  auditId?: string;
}

/** POST /api/chat/quick-replies/select — the employer picked a suggestion. */
export interface QuickReplySelectRequest {
  auditId: string;
  selectedId: string;
  selectedText: string;
}

// --- Assistant ---
// Shown in place of quick replies when the backend says the assistant is on.
// The two lanes stay separate so neither can regress the other.

/**
 * POST /api/chat/assistant. No transcript and no candidate profile: the upstream
 * reads those itself, so nothing the client sends can reach the prompt.
 */
export interface AssistantReadingRequest {
  channelId: string;
}

/** The languages a reading's drafts can be shown in: the jobseeker sites' three. */
export type AssistantLanguage = FormLanguage;

/** The one move an assistant reading may name. */
export type AssistantSuggestion = 'shortlisted' | 'interview' | 'hired' | 'end' | 'message';

/**
 * `enabled: false` means the assistant is off for this employer. An enabled
 * response with empty `intro` and no `suggest` is a reading with nothing to
 * recommend, which is expected. `recommended` is set only when `suggest` is
 * `message`. `auditId` is absent when the reading could not be stored.
 */
export interface AssistantReadingResponse {
  enabled: boolean;
  /**
   * A re-read is outstanding and no reading has landed since. What follows is
   * the last stored reading; the client shows the waiting line over it until
   * this clears (the backend expires the stamp itself after five minutes).
   */
  pending: boolean;
  /** The lane is on but the BFF could not reach a reading. The footer draws
   *  nothing rather than let an outage read as a decision. */
  failed?: boolean;
  auditId?: string;
  intro: string;
  suggest: AssistantSuggestion | null;
  replies: string[];
  /**
   * The same fact the inbox row's dot is drawn from. Distinct from deriving it
   * from `suggest`: during a re-read the live verdict is withdrawn and this one
   * stands, because it is the judgement the row is still wearing.
   */
  flagged?: boolean;
  /**
   * Which draft the assistant actually recommended, as an index into `replies`.
   *
   * Absent when it named none, which is most readings — the drafts are usually
   * alternatives rather than a ranking. Absent is not zero: the card draws no
   * pill for it, because "best first" is an ordering and the pill is a claim
   * about one specific draft.
   */
  recommended?: number;
  /** The language the drafts were written in. Absent means English. */
  language?: AssistantLanguage;
}

/** POST /api/chat/translate — any text the employer is looking at, via chat-kit. */
export interface TranslateRequest {
  language: FormLanguage;
  text: string;
}

export interface TranslateResponse {
  text: string;
}

export const RESUME_FORMATS = ['pdf', 'zip', 'ole', 'html', 'empty', 'unknown'] as const;
export const RESUME_ERROR_NAME_MAX = 100;
export const RESUME_ERROR_MESSAGE_MAX = 500;

/** POST /api/chat/resume-render-error — pdf.js failed in the browser after the file arrived. */
export interface ResumeRenderErrorReport {
  stage: 'load' | 'render';
  errorName: string;
  errorMessage: string;
  size: number;
  /** Sniffed from the leading bytes; a label, never the bytes themselves. */
  format: (typeof RESUME_FORMATS)[number];
  page?: number;
  pages?: number;
  /** The worker asset path; a hash the deployed build no longer serves means a stale tab. */
  worker: string;
}

export const BOUNDARY_PLACES = ['details-panel', 'resume-viewer'] as const;

/** POST /api/chat/boundary-error — an ErrorBoundary caught a render crash in the browser. */
export interface BoundaryErrorReport {
  where: (typeof BOUNDARY_PLACES)[number];
  errorName: string;
  errorMessage: string;
}

/** POST /api/chat/assistant/select — the employer took something from the card. */
export interface AssistantSelectRequest {
  auditId: string;
  takenKind: 'action' | 'reply';
  takenValue: string;
  edited?: boolean;
  /** The language the taken draft was shown in. */
  language?: AssistantLanguage;
}

// --- Façade DTOs (jobs / candidates / applications) ---
// The owning company is NEVER part of a client DTO; ownership is enforced
// server-side from the session identity.

export type ApplicationState = 'shortlisted' | 'hired' | 'rejected';

/** GET/POST /api/applications/:id — an application as the client sees it. */
export interface ApplicationDto {
  id: string;
  candidateName: string;
  jobTitle: string;
  state: ApplicationState | 'applied';
}

/** POST /api/applications/:id/state */
export interface SetApplicationStateRequest {
  state: ApplicationState;
}

/** GET /api/jobs — an active job in the New Chat picker. */
export interface ActiveJobDto {
  id: string;
  title: string;
  jobType: string;
  location?: string;
  applicantCount: number;
}

/** GET /api/jobs/:id/applicants — one applicant row in the New Chat picker. */
export interface ApplicantDto {
  /** Jobseeker userid — stable, the future Stream member id. */
  id: string;
  name: string;
  avatarUrl?: string;
  /** Upstream 'pending' maps to 'new'; other pipeline stages are one-to-one. */
  status: 'new' | 'shortlisted' | 'interview' | 'hired' | 'rejected';
}

/**
 * What the upstream profile form offers, and therefore everything a candidate's
 * gender can be. A document that says anything else says nothing this app can
 * render, and arrives here as absent.
 */
export const GENDERS = ['Male', 'Female'] as const;
export type Gender = (typeof GENDERS)[number];

/** A candidate as the details panel shows them. */
export interface CandidateDto {
  name: string;
  avatarUrl?: string;
  /** Absent when the profile does not carry one. */
  gender?: Gender;
  /**
   * Whole years, derived server-side. The date of birth stays upstream: the
   * panel renders a number, so a number is all that travels.
   */
  age?: number;
  /** Where the candidate lives, pre-joined, e.g. "Gopeng, Perak, Malaysia".
   *  Absent when the profile names no part of it. */
  domicile?: string;
  /** Where they are willing to work, each pre-joined like `domicile`. Empty when
   *  the profile states none. */
  preferredLocations: string[];
  experienceYears: number;
  /** `period` is "Aug 2022 – Present"; `tenure` is "3 yrs 10 mo", both end months
   *  counted (empty when the dates make no sense). */
  experiences: { title: string; company: string; period: string; tenure: string }[];
  skills: string[];
  /** Pre-formatted, e.g. "RM 9,000". Absent when the source has no amount. */
  expectedSalary?: string;
  languages: string[];
  /**
   * The highest rung reached, on the same ladder the inbox filter offers —
   * absent when the profile names none this app recognises. Typed off
   * `EDUCATION_LEVELS` so the panel can never name a rung the filter cannot.
   */
  education?: EducationLevel;
  /** Malaysian or Non-Malaysian, the two words the inbox filter offers. Absent
   *  when the profile does not say. Ungated, like gender and age. */
  nationality?: Nationality;
  /** Present only when this company has unlocked the candidate. */
  contact?: { email?: string; phone?: string };
  /**
   * Present only when unlocked AND a résumé is attached. Presence is the whole
   * authorization signal, exactly as before.
   *
   * Carries no name and no URL. The bytes come from
   * `GET /api/conversations/:cid/resume`, which requires this app's session, and
   * the filename arrives with them — so neither is published to every client
   * that opens a conversation, and a name too long for the chip cannot reach it.
   */
  resume?: { available: true };
  /** Whether this company has unlocked the candidate: the server's answer, not inferred. */
  unlocked: boolean;
}

/**
 * The application this conversation is about (job-bounded chats only).
 *
 * No application id, for the same reason `JobDetailDto` carries no job id: the
 * panel renders none of it, and every route that acts on the application
 * (`applicant-status`) re-resolves it from the channel rather than trusting an
 * id the client hands back.
 */
export interface ApplicationSummaryDto {
  /** ISO-8601. */
  appliedAt: string;
  status: 'new' | 'shortlisted' | 'interview' | 'hired' | 'rejected';
}

/** The full posting: the panel's "View job details" dialog and GET /api/jobs/:id.
 *  No job id — the caller already has it; a second copy could disagree. */
export interface JobDetailDto {
  title: string;
  jobType: string;
  location?: string;
  /** The board's pre-formatted label (e.g. "RM 6,000 - RM 9,000 / month"), never recomposed. */
  salary?: string;
  description?: string;
  responsibilities?: string[];
  requirements?: string[];
  /** The posting's company, for the card head. */
  company?: { name: string; logoUrl?: string; verified?: boolean };
}

/** GET /api/conversations/:cid/details */
export interface ConversationDetailsDto {
  candidate: CandidateDto;
  /** Present only for a job-bounded conversation. */
  application?: ApplicationSummaryDto;
  job?: JobDetailDto;
}

/** A message template as the client sees it (company system template or the
 *  user's own). No company/user id ever reaches the client. */
export interface TemplateDto {
  /** chat-kit's 32-hex UUIDv7; opaque to the client, handed back on update/delete. */
  id: string;
  name: string;
  template: string;
  isSystem: boolean;
}

/** POST/PUT /api/templates body. Limits (name ≤100, template ≤350 runes) are chat-kit's. */
export interface SaveTemplateRequest {
  name: string;
  template: string;
}

/** A contact in the employer's book (Contacts PoC, kept in chat-kit). */
export interface ContactDto {
  id: string;
  name: string;
  email: string;
  createdAt: string;
}

/** POST /api/contacts body: with `id` it renames, without it adds. */
export interface SaveContactBody {
  id?: string;
  name: string;
  email: string;
}

/** POST /api/contacts/start body: the contact and the first message. */
export interface StartContactChatBody {
  contactId: string;
  text: string;
}

/**
 * How long an auto reply may be, in characters.
 *
 * The composer's own template cap, which the store upstream mirrors — it counts
 * runes and answers 422 past this. Exported so the route validator and the
 * pane's textarea read one number rather than two that can drift.
 */
export const AUTO_REPLY_MESSAGE_MAX = 350;

/**
 * The wording an employer who has never written one starts from.
 *
 * A draft, never a stored reply: the pane opens with this in the box and the
 * account sends nothing until somebody reads it, keeps or rewrites it, and
 * presses Save. See `useAutoReply`, which is the only place it is used — the
 * BFF neither sends it nor substitutes it, so what a read answers stays the
 * honest zero value.
 *
 * Written the way an employer writes rather than the way a default usually is:
 * short, no greeting placeholder to substitute a name into, honest that a person
 * is coming, and it asks for the questions rather than announcing them — the
 * form is optional, and this reads as a whole message without one.
 */
export const AUTO_REPLY_DEFAULT_MESSAGE =
  'Thanks for getting in touch. While we look at your profile, could you answer a few quick questions? It takes about a minute and helps us get back to you faster.';

/**
 * GET/PUT /api/auto-reply — the one greeting this account sends, whole.
 *
 * One reply per account, so there is no id and no list: the read answers the
 * row (or its zero value, for an employer who has never saved one) and the write
 * replaces it.
 */
export interface AutoReplyDto {
  enabled: boolean;
  message: string;
  /**
   * The form the reply carries, or `null` for a plain holding message.
   *
   * `null` rather than optional: "no form" must be a value the client can read,
   * not an absent key it has to tell apart from "not loaded yet". Opaque to both
   * sides of the wire — the store never resolves it.
   */
  formId: string | null;
}

/** PUT /api/auto-reply body. The whole reply; a missing `formId` is not a
 *  no-op, it clears the stored one. */
export type SaveAutoReplyRequest = AutoReplyDto;

/** Statuses the applicant-status route persists: the three hiring stages (via the
 *  split-button) plus `rejected` (via the End & reject flow). */
export type HiringStatusWrite = 'shortlisted' | 'interview' | 'hired' | 'rejected';

/** The page that embeds chat, as ajt2.5 records it. Only the host can say, at `init`. */
export const UI_ORIGINS = ['am.chat'] as const;
export type UiOrigin = (typeof UI_ORIGINS)[number];

export function isUiOrigin(value: unknown): value is UiOrigin {
  return UI_ORIGINS.includes(value as UiOrigin);
}

/** POST /api/conversations/:cid/applicant-status body. */
export interface SetApplicantStatusRequest {
  status: HiringStatusWrite;
  ui_origin?: UiOrigin;
}

/** POST /api/chat/end-chat — end a conversation: the backend sends the closing
 *  message and stamps the channel `closing`. The sending user is injected by the
 *  BFF from the session, never from this body. */
export interface EndChatRequest {
  channelId: string;
  text: string;
  /**
   * The employer ended this chat by rejecting the applicant, so the caller also
   * moved them to `rejected`. Descriptive only: it labels the analytics the BFF
   * emits after the close, and the two writes stay separate endpoints upstream.
   */
  alsoRejected?: boolean;
}

export const REPORT_REASONS = [
  'spam',
  'harassment',
  'fake_profile',
  'inappropriate',
  'money_or_personal_details',
  'other',
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];
export const JOBSEEKER_REPORT_REASONS = [
  'spam',
  'harassment',
  'fake_profile',
  'inappropriate',
  'other',
] as const satisfies readonly ReportReason[];
export const MESSAGE_REPORT_REASONS = [
  'spam',
  'harassment',
  'inappropriate',
  'money_or_personal_details',
  'other',
] as const satisfies readonly ReportReason[];
export const REPORT_DETAILS_MAX = 500;

/** POST /api/chat/report — report the jobseeker in a channel, or one of their
 *  messages when `messageId` is set. The reporter is the session, never this body,
 *  and the reported jobseeker is read off the channel upstream. */
export interface ReportRequest {
  channelId: string;
  messageId?: string;
  reason: ReportReason;
  /** Required when `reason` is `other`. */
  details?: string;
}

/** POST /api/chat/edit-message — replace the text of the employer's own message. */
export interface EditMessageRequest {
  channelId: string;
  messageId: string;
  text: string;
}

/**
 * Stream's own per-file ceiling is 100MB, but a recruiter attaching a CV or a
 * screenshot has no use for that, and a large upload on a phone stalls the composer.
 */
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/** A chat file id as the backend accepts it: a lowercase version 4 uuid. */
export const CHAT_FILE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/** POST /api/chat/files — start an upload into our own bucket. 404 `chat_files_off` while uploads are off. */
export interface ChatFileUploadRequest {
  channelId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface ChatFileUploadResponse {
  fileId: string;
  /** A presigned POST: every field, then the file, as multipart form data. */
  upload: { url: string; fields: Record<string, string> };
  /** The Stream attachment the backend built; posted on the message unchanged. */
  attachment: Record<string, unknown>;
}

/** POST /api/chat/files/urls — short-lived links for files in one channel. */
export interface ChatFileUrlsRequest {
  channelId: string;
  fileIds: string[];
}

export interface ChatFileLink {
  url: string;
  thumbUrl: string | null;
  expiresAt: string;
}

/** Unknown, deleted or foreign ids are left out. */
export interface ChatFileUrlsResponse {
  files: Record<string, ChatFileLink>;
}

/** POST /api/cta-click — a CTA button below a message was tapped. The backend
 *  records the campaign-funnel event and seals the row. The clicking user is
 *  injected by the BFF from the session, never from this body. */
export interface CtaClickRequest {
  channelId: string;
  messageId: string;
  /** The clicked button's `id`. */
  ctaId: string;
}

// --- Analytics ---
// Product-analytics events, batched by the client and forwarded by the BFF to
// PostHog (via stream-chat-backend). The client sends ONLY the events (plus its
// own render mode); the BFF injects the user identity from the verified session,
// never from client input — the same rule as every other route.

/** One product-analytics event in a batch. */
export interface AnalyticsEvent {
  /** Event name, e.g. `chat_opened`. The backend allowlists accepted names. */
  event: string;
  /** ISO-8601. */
  timestamp: string;
  properties?: Record<string, unknown>;
}

/** POST /api/chat/analytics/track — a batch of 1–100 events. */
export interface AnalyticsTrackRequest {
  events: AnalyticsEvent[];
  /** The client's render mode, for segmentation. */
  viewMode?: 'mobile' | 'desktop';
}

/**
 * Body of `POST /api/chat/start` — create-or-resume a conversation and post the
 * first message, as one call.
 *
 * The employer is deliberately absent: it comes from the verified session, so a
 * caller cannot open a channel as another company. `source` and `sourceMeta` are
 * opaque and travel upstream verbatim — see `StartChatTarget`.
 */
export interface StartChatRequest {
  applicant: { id: string; name: string; image?: string };
  company: { name: string; image?: string };
  source: string;
  sourceMeta?: Record<string, unknown>;
  /** Present only for a job-bound source. */
  job?: { id: string };
  text: string;
}

export interface StartChatResponse {
  channelId: string;
}

/** POST /api/chat/draft — what the pane needs before a chat exists. Creates nothing. */
export interface DraftChatRequest {
  applicant: { id: string };
  source: string;
  job?: { id: string };
}

export interface DraftChatResponse {
  /** The conversation already exists: open it instead. */
  channelId?: string;
  /** Job-bound sources only. */
  application?: ApplicationSummaryDto;
  jobTitle?: string;
}

/** POST /api/chat/draft/status — a hiring status write with no chat behind it. */
export interface DraftStatusRequest {
  applicant: { id: string };
  job: { id: string };
  status: HiringStatusWrite;
  ui_origin?: UiOrigin;
}

/** One of the company's postings into a chat that already exists, as the card the
 *  composer sends. Identity comes from the session; the channel and the job are
 *  both checked for ownership. */
export interface SendJobRequest {
  channelId: string;
  jobId: string;
  /** The employer's words above the card; absent, the card's title stands in. */
  text?: string;
}

export interface SendJobResponse {
  channelId: string;
}

/**
 * What is written about a conversation, as the BFF returns it: a note the
 * employer typed and a summary a model drew. Either half may be absent.
 *
 * Scoped to the channel rather than the user — the panel's padlock promises the
 * employer's team sees this, and the team is who is on the channel. Never shown
 * to the jobseeker.
 */
export interface ConversationDocsDto {
  note: { text: string; updatedAt: string } | null;
  summary: {
    text: string;
    /** The last message the draw accounted for. */
    coversUpTo: string;
    /** The note as it stood when the summary was drawn; `''` means there was none. */
    drawnWithNote: string;
    generatedAt: string;
  } | null;
}

/** Body of `POST /api/docs/note`. An empty `text` clears the note. */
export interface SaveNoteBody {
  channelId: string;
  text: string;
}

/** Body of `POST /api/docs/summary` — the accepted draw, with the inputs it was drawn from. */
export interface SaveSummaryBody {
  channelId: string;
  text: string;
  coversUpTo: string;
  drawnWithNote: string;
}

/**
 * Body of `POST /api/docs/summary/draw` — draws a fresh summary without storing
 * it. The transcript and the employer's note are read upstream from the channel,
 * never sent from here, so a doctored transcript cannot reach the model.
 */
export interface DrawSummaryBody {
  channelId: string;
}

/**
 * What `POST /api/docs/summary/draw` returns: the drawn text and the last message
 * it accounted for. Nothing is stored until the employer keeps it (SaveSummaryBody).
 */
export interface DrawnSummaryDto {
  text: string;
  coversUpTo: string;
}

/** How long either entry may be. Mirrors the upstream column and the editor's cap. */
export const DOC_TEXT_LIMIT = 500;

export function isApiError(body: unknown): body is ApiError {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as Record<string, unknown>;
  return (
    typeof b.code === 'string' && typeof b.message === 'string' && typeof b.retryable === 'boolean'
  );
}

/**
 * `cancelled`, `rejected` and `rescheduled` are deliberately absent: a
 * `rescheduled` row is a tombstone (the service creates a new appointment), so
 * keeping it would draw every moved interview twice. The BFF adapter drops them.
 */
export type AppointmentStatus =
  'pending' | 'awaiting_approval' | 'confirmed' | 'reschedule_requested' | 'completed';

/**
 * A separate axis from `AppointmentStatus`, which is the appointment's rolled-up
 * state. The vocabularies are deliberately not shared (`responseLabel` beside
 * `statusLabel`). `no_response` is a word
 * rather than an absence because it is the state the employer acts on.
 */
export type InviteeResponse = 'no_response' | 'accepted' | 'rejected';

/**
 * Somebody an appointment is for.
 *
 * Two ids, and they are not interchangeable. `userId` is the Stream member form
 * (`js_…`), which lines up with channel members without a
 * second mapping; `id` is calendar-service's invitee ROW id, which is what a
 * response is recorded against. Merging them would be a saving of one field and
 * a permanent source of answering on the wrong person's behalf.
 */
export interface EventInviteeDto {
  /**
   * calendar-service's invitee row id.
   *
   * Optional, and absent means exactly one thing: an appointment predating the
   * invitee table, whose guest is derived from the legacy columns and so has no
   * row to answer against. Everything the upstream returns today carries one.
   * Nothing on screen reads it — it exists so an invite's Accept / Decline can
   * name the row the answer belongs to.
   */
  id?: number;
  userId: string;
  name: string;
  response: InviteeResponse;
  /** nil upstream means required — see the upstream's `InviteeRequest`. */
  required: boolean;
}

/**
 * Something the employer has scheduled, as the Event section reads it.
 *
 * Normalised from calendar-service's appointment, which is an organizer plus N
 * invitees: one jobseeker on an ordinary interview, twenty-four on a Chat Day.
 * A start plus a duration, and a free-text venue. Reminders and an event kind
 * still have no source, so they are not modelled here.
 */
export interface CalendarEventDto {
  id: string;
  /** The upstream title, or "Interview" where it left one unset. */
  title: string;
  startsAt: string;
  /** Computed at the adapter from `scheduled_at` + `duration_minutes`, because
   *  every placement function in the grid works on a span. */
  endsAt: string;
  status: AppointmentStatus;
  /**
   * An array even for one invitee, because the upstream models it that way.
   * An appointment with no invitee list falls back to the legacy columns.
   */
  invitees: EventInviteeDto[];
  /** Exactly as the employer typed it. May be empty. */
  location: string;
  /** What to expect on the day. Absent where none was written. */
  description?: string;
}

/**
 * Booking a Chat Day: one appointment, every guest an invitee.
 *
 * The guests are named by their **channels**, not by their user ids. That is
 * the ownership check and the identity resolution in one move — the BFF derives
 * each jobseeker from a channel it has proved the caller is a member of, so a
 * body cannot name somebody the employer has no chat with. The employer
 * themselves is never on here at all; that comes from the verified session.
 */
export interface CreateChatDayRequest {
  title: string;
  /** ISO instant. */
  startsAt: string;
  durationMinutes: number;
  /** What to expect on the day. Optional. */
  description?: string;
  /** Stream channel ids or cids, one per guest. See `MAX_CHAT_DAY_GUESTS`. */
  channelIds: string[];
}

/**
 * The upstream's invitee cap, mirrored so the refusal is ours and says so.
 *
 * Here rather than on either side because both need it and they must not drift:
 * the BFF refuses a longer list, and the client's invite fan-out uses it as the
 * ceiling on its own loop. A client capped lower than the server accepts is how
 * a booking succeeds and quietly notifies the first twenty.
 */
export const MAX_CHAT_DAY_GUESTS = 50;

/**
 * Where a marketing Chat Day invitation stands for the caller's company, as
 * chat-kit reads it: `open` while Jobie's card can still be answered, else why
 * not. `closed` is a link generated before this company answered.
 */
export type ChatDayInvitationState = 'open' | 'accepted' | 'declined' | 'closed' | 'ended';

/** POST /api/chat-day/invitations — the invite codes on the cards in view. */
export interface ChatDayInvitationsRequest {
  inviteCodes: string[];
}

/** Keyed by invite code. A code chat-kit does not know for this company is absent. */
export interface ChatDayInvitationsResponse {
  invitations: Record<string, ChatDayInvitationState>;
}

/** Adding guests to an existing Chat Day. Named by channel, as at booking. */
export interface AddChatDayGuestsRequest {
  channelIds: string[];
}

export interface AddChatDayGuestsResponse {
  /** The appointment after the adds, status re-derived upstream. */
  event: CalendarEventDto;
  /** Channel ids whose guest was added. Their row ids are on `event`. */
  added: string[];
  /** Channel ids whose guest could not be added. Nobody here gets an invite. */
  failed: string[];
  /** True when the added guests were filed into the Chat Day's list, so it needs a re-read. */
  listsChanged?: boolean;
}

// ── forms (chat-kit lane) ────────────────────────────────────────────────────

/** One question on a form, in chat-kit's own vocabulary — camelCase
 *  `multiChoice` included. The same shape rides Stream messages inside the
 *  dispatch notice, so client, BFF and chat-kit never translate field types. */
export type FormFieldTypeDto = 'choice' | 'multiChoice' | 'text';

export interface FormFieldDto {
  id: string;
  label: string;
  /** Optional words under the question; absent when blank. */
  description?: string;
  type: FormFieldTypeDto;
  /** Always empty on a `text` question. */
  options: { id: string; label: string }[];
}

/** The longest description under a question, as chat-kit stores it. */
export const MAX_DESCRIPTION = 200;

/** The three languages the jobseeker sites speak. A form is written in one of
 *  them; a blank form has none until the employer decides. */
export const FORM_LANGUAGES = ['en', 'ms', 'zh'] as const;
export type FormLanguage = (typeof FORM_LANGUAGES)[number];

/** The catalogue's three groups, as the template document names them. Set by
 *  chat-kit's seed migrations only; never accepted from a client. */
export type TemplateCategory = 'general' | 'non_executive' | 'executive';
export const TEMPLATE_CATEGORIES: readonly TemplateCategory[] = [
  'general',
  'non_executive',
  'executive',
];

/** A form as the client sees it. `id` is chat-kit's 32-hex UUIDv7; versioning
 *  stays server-side — the client always reads the current version. */
export interface FormDto {
  id: string;
  name: string;
  isTemplate: boolean;
  /** Only the catalogue's 50 carry a category; the three legacy templates and
   *  every employer form are null. */
  category: TemplateCategory | null;
  language: FormLanguage | null;
  fields: FormFieldDto[];
  updatedAt: string;
}

/** POST /api/forms/save. Absent formId creates; present appends a version. */
export interface SaveFormBody {
  formId?: string;
  name: string;
  fields: FormFieldDto[];
  language?: FormLanguage;
}

/** POST /api/forms/dispatch: record the send and get the payload to post. */
export interface DispatchFormBody {
  formId: string;
  /** The Stream cid of the conversation the form is going into. */
  channelId: string;
}

export interface FormDispatchDto {
  dispatchId: string;
  /** The `form` custom field to post on the Stream message, verbatim — the
   *  delivery client copies rather than assembles (chat-kit docs/API.md). */
  notice: Record<string, unknown>;
}

/** POST /api/forms/dispatch-link: name the message the dispatch became. */
export interface LinkDispatchBody {
  dispatchId: string;
  messageId: string;
}

/** One answer as chat-kit recorded it: picks by option id, or the written words. */
export interface FormAnswerDto {
  fieldId: string;
  optionIds: string[];
  text?: string;
}

/** The profile facts chat-kit looks up by jobseeker id for the Responses table. */
export interface FormResponseProfileDto {
  name: string | null;
  expectedSalary: number | null;
  city: string | null;
  education: string | null;
  languages: string[];
}

/** One row of a form's Responses table: the answer against the questions AS
 *  ASKED, where and when it was asked, and the words chat-kit resolved by id.
 *  A chat deleted by retention still lists; `jobTitle` and `profile` are null
 *  when chat-kit could not say. */
export interface FormResponseDto {
  submissionId: string;
  formId: string;
  formName: string;
  fields: FormFieldDto[];
  answers: FormAnswerDto[];
  channelCid: string;
  jobseekerId: string;
  sentAt: string;
  submittedAt: string;
  jobId: string | null;
  jobTitle: string | null;
  profile: FormResponseProfileDto | null;
}

/** POST /api/forms/responses: every answer to one form across chats. */
export interface ListFormResponsesBody {
  formId: string;
}

export interface FormResponsesDto {
  responses: FormResponseDto[];
  /** More exist past the newest 1,000 the BFF asked for. */
  truncated: boolean;
}

/** One message a search matched — a row of the inbox's Messages group. */
export interface MessageHitDto {
  /** Bare channel id, the name the client's Stream hydration knows it by. */
  channelId: string;
  messageId: string;
  /** The message's words, raw; the client windows and marks them. */
  text: string;
  createdAt: string;
}

/** GET /api/inbox/message-hits?q= — the messages behind a search, newest first. */
export interface MessageHitsResponse {
  hits: MessageHitDto[];
  /** Stream could not answer, so there are no hits to show and it is not
   *  "nothing matched". Same meaning as the inbox query's flag. */
  message_search_unavailable?: boolean;
}
